import React, { useEffect } from "react";
import { SectionWrapper } from "../../../../../UI/SectionWrapper/SectionWrapper";
import { Accordion, AccordionSummary, Grid } from "@mui/material";
import { ExpandMore } from "@mui/icons-material";
import { AssetAccordionDetails } from "../../../../ui-components/pageLayout/TableSettingsComponents";
import { FormField } from "../../../../../Form/FormField";
import { CMSTag } from "../../../../../../models/CMS/cmsTag";
import { useFormikContext } from "formik";

const BasicInformationAccordion: React.FC<{
  providers?: CMSTag[];
}> = ({ providers = [] }) => {
  const { values, setFieldValue } = useFormikContext<any>();

  // Find the current provider tag from the tags array
  const currentProviderTag = (values.tags || []).find(
    (tag: any) => tag.type === "provider"
  );

  // Sync the provider.providerId field with the current provider tag
  useEffect(() => {
    if (currentProviderTag) {
      setFieldValue("provider.providerId", currentProviderTag.tagId);
    }
  }, [currentProviderTag?.tagId, setFieldValue]);

  // Watch for changes to the provider.providerId field and update tags array
  useEffect(() => {
    const providerId = values.provider?.providerId;

    // If empty or undefined, remove provider from tags
    if (!providerId || providerId === "") {
      const tagsWithoutProvider = (values.tags || []).filter(
        (tag: any) => tag.type !== "provider"
      );
      if (tagsWithoutProvider.length !== (values.tags || []).length) {
        setFieldValue("tags", tagsWithoutProvider);
      }
      // Clear the provider object entirely
      setFieldValue("provider", undefined);
      return;
    }

    const selectedProvider = providers.find((p) => p.tagId === providerId);
    if (!selectedProvider) return;

    // Check if this provider is already set
    if (currentProviderTag?.tagId === providerId) return;

    // Remove any existing provider tags from the tags array
    const tagsWithoutProvider = (values.tags || []).filter(
      (tag: any) => tag.type !== "provider"
    );

    // Add the new provider tag
    setFieldValue("tags", [
      ...tagsWithoutProvider,
      selectedProvider.serialize(),
    ]);
  }, [
    values.provider?.providerId,
    providers,
    currentProviderTag?.tagId,
    setFieldValue,
  ]);

  return (
    <SectionWrapper>
      <Accordion defaultExpanded>
        <AccordionSummary
          expandIcon={<ExpandMore />}
          className="accordion-summary-gray"
        >
          Basic information
        </AccordionSummary>
        <AssetAccordionDetails>
          <Grid container spacing={2}>
            <Grid item xs={6}>
              <FormField
                name="originalTitle"
                label="Original Title"
                placeholder="Asset name used in the Admin Portal..."
              />
            </Grid>
            <Grid item xs={6}>
              <FormField
                name="originalLanguage"
                label="Original Language"
                placeholder="Original language..."
              />
            </Grid>
            <Grid item xs={6}>
              <FormField
                name="countryOfOrigin"
                label="Country Of Origin"
                placeholder="Country of origin..."
              />
            </Grid>
            <Grid item xs={6}>
              <FormField
                name="copyrightInfo"
                label="Copyright Info"
                placeholder="Copyright info..."
              />
            </Grid>
            <Grid item xs={6}>
              <FormField
                name="duration"
                label="Duration"
                placeholder="Duration..."
              />
            </Grid>
            <Grid item xs={6}>
              <FormField
                name="productionYear"
                label="Production Year"
                placeholder="Production year..."
              />
            </Grid>
            <Grid item xs={6}>
              <FormField
                name="releaseYear"
                label="Release year"
                placeholder="Release year..."
              />
            </Grid>
            <Grid item xs={6}>
              <FormField name="studio" label="Studio" placeholder="Studio..." />
            </Grid>
            <Grid item xs={6}>
              <FormField name="eidrId" label="EidrId" placeholder="EidrId..." />
            </Grid>
            {providers.length > 0 && (
              <Grid item xs={6}>
                <FormField
                  name="provider.providerId"
                  label="Provider"
                  select
                  options={[
                    { value: "", label: "No provider" },
                    ...providers.map((provider) => ({
                      value: provider.tagId,
                      label: provider.name,
                    })),
                  ]}
                />
              </Grid>
            )}
          </Grid>
        </AssetAccordionDetails>
      </Accordion>
    </SectionWrapper>
  );
};

export default BasicInformationAccordion;
